import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
	rectSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import {
	InsertSponsor,
	UpdateSponsor,
} from '../../../../../../connection/sponsors';
import { UploadFile } from '../../../../../../connection/uploads';
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import {
	addSponsor,
	loadSponsors,
	updateSponsor,
} from '../../../../../../store/actions/admin/sponsors';
import { EventsState } from '../../../../../../store/types';
import {
	GetDefaultSponsor
} from '../../../../../../store/utils/create-event';
import { SponsorsLayoutTypesClassic, TemplateClassNames } from '../../../../../../types/template-layouts';
import {
	ColorKeys,
	ContentEditors,
	CreateSponsor,
	LanguagesAbbr,
	PageModuleType,
	Sponsor,
	TextOverrides,
} from '../../../../../../types/working-model';
import { getDefaultModuleAlignment, getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import LargeButton from '../../../../../general-ui/button/large-button';
import Switch from '../../../../../general-ui/switch/switch';
import TextInput, { Validation } from '../../../../../general-ui/text-input/text';
import Textarea from '../../../../../general-ui/textarea/textarea';
import ModalComponent from '../../../../../general-ui/modal/modal';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import AddSponsor from '../add-sponsor';
import SponsorComponent from './sponsor';
import '../../../../../../scss/live-event/base/modules/sponsors.scss';
import './sponsors.scss';
import Controls from '../../../session/editor/moduleControls/controls';
import { getStylingOverrides } from '../../../../../live-event/utils';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import ImageEditor from '../../../../../general-ui/image-editor/image-editor';
import CallToActionForm from '../../../../../general-ui/call-to-action/call-to-action';
import useRequiredFields from '../../../../../../utils/use-required-fields';
import { BasicLangingModuleProps } from '../editor-main';
import { Tooltip } from '../../../../../general-ui/tooltip/tooltip';
import EditableTextDraftQuill from '../../../../../general-ui/editable-text/editable-text-draft-quill';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';

interface Props extends BasicLangingModuleProps {
	handleScroll: (moduleId?: number, p?: PageModuleType) => void;
}

const EditorSponsors: React.FC<Props> = React.forwardRef<HTMLDivElement, Props>(({ page_module, template, handleScroll, language, baseLanguage }, ref) => {
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);
	const {
		AuthReducer: { token, user },
		SponsorsReducer: { sponsors },
		CreateEventReducer: {
			workingEvent
		}
	} = useSelector((event: EventsState) => event);

	if (!page_module?.modules?.[0]) { //saftey check for null values in .modules
		page_module.modules = [];
	}

	const [modalOpen, setModalOpen] = useState(false);
	const [workingSponsor, setWorkingSponsor] = useState<
		Sponsor | CreateSponsor | null
	>(null);
	const [displayImageEditor, setDisplayImageEditor] = useState(false);
	const [editSponsorImage, setEditSponsorImage] = useState<File | FileList | null>(null);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [sponsorToRemove, setSponsorToRemove] = useState<Sponsor | null>(
		null
	);
	const [editDisplayedSponsor, setEditDisplayedSponsor] = useState(false);
	const [visibleSponsors, setVisibleSponsors] = useState<Sponsor[]>(
		page_module.modules ?? []
	);
	const [, setSearchTerm] = useState('');
	const [uploading, setUploading] = useState(false);
	const [selectedSponsors, setSelectedSponsors] = useState<number[]>(
		page_module.content_modules ?? []
	);
	const dispatch = useDispatch();

	const content = page_module?.content;

	const styling_overrides = page_module?.styling_overrides;
	const {
		backgroundColor,
		backgroundImg,
	} = getStylingOverrides(styling_overrides);
	const layout_type = page_module.content?.layout_type || SponsorsLayoutTypesClassic.standard;

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const {
		requiredFields,
		hasCompletedRequiredFields,
		updateRequiredField,
		resetRequiredFields
	} = useRequiredFields(['name', 'image']);

	const handleTextChange = useCallback((key: string) => {
		return (text: string) => {
			dispatch(
				updateHomepageModule(
					{
						...page_module,
						content: {
							...content,
							[key]: updateTranslateKey({
								translateString: page_module?.content?.[key],
								input: text,
								language,
								baseLanguage
							})
						},
						is_edited: true,
					}
				)
			);
		};
	}, [baseLanguage, content, dispatch, language, page_module]);

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					is_edited: true,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	function editSponsor(sponsor: Sponsor) {
		return () => {
			setWorkingSponsor(sponsor);
			if (!modalOpen) setEditDisplayedSponsor(true);
			setModalOpen(true);
		};
	}

	function closeDeleteModal() {
		setConfirmingDelete(false);
		setSponsorToRemove(null);
	}

	function checkDeleteSponsor(sponsor: Sponsor) {
		return () => {
			setSponsorToRemove(sponsor);
			setConfirmingDelete(true);
		};
	}

	function handleDragEnd(event: any) {
		const _modules = [...(page_module.modules ?? [])];

		const startIndex = _modules.findIndex(
			(sponsor: Sponsor) =>
				Number(sponsor.sponsor) === Number(event.active.id)
		);
		const newIndex = _modules.findIndex(
			(sponsor: Sponsor) =>
				Number(sponsor.sponsor) === Number(event.over.id)
		);

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updateHomepageModule({
				...page_module,
				is_edited: true,
				modules: _modules,
				content_modules: _modules.map(
					(sponsor: Sponsor) => sponsor.sponsor
				),
			})
		);
	}

	function openModal() {
		setModalOpen(true);
	}

	function closeImageEditor() {
		setEditSponsorImage(null);
		setDisplayImageEditor(false);
	}

	function closeModal() {
		setModalOpen(false);
		setWorkingSponsor(null);
		closeImageEditor();
	}

	function cancel() {
		if (workingSponsor) {
			resetRequiredFields();
			if (editSponsorImage) {
				closeImageEditor();
				return;
			}
			if (editDisplayedSponsor) {
				setModalOpen(false);
				setEditDisplayedSponsor(false);
				// stop modal from flashing all sponsors while closing
				setTimeout(() => setWorkingSponsor(null), 300);
			} else {
				setWorkingSponsor(null);
			}
		} else {
			closeModal();
		}
	}

	const modalTitle = useMemo(() => {
		if (!workingSponsor) {
			return 'Add Sponsor';
		} else if ('sponsor' in workingSponsor) {
			return 'Edit Sponsor';
		} else {
			return 'Add New Sponsor';
		}
	}, [workingSponsor]);

	async function finishedAddingSponsor() {
		if (!token) return;
		if (!workingSponsor) {
			dispatch(
				updateHomepageModule({
					...page_module,
					is_edited: true,
					modules: sponsors.filter((sponsor: Sponsor) =>
						selectedSponsors.includes(sponsor.sponsor)
					),
					content_modules: selectedSponsors,
				})
			);
			setModalOpen(false);
		} else {
			// when modal is used to edit the speaker image

			if (editSponsorImage) {
				await handleFile(editSponsorImage as File);
				closeImageEditor();
				return;
			}
			if (!hasCompletedRequiredFields(workingSponsor as any)) return;
			const _sponsor = { ...workingSponsor };
			//we're editing a sponsor, not creating a new one
			if ('sponsor' in _sponsor) {
				if (workingEvent?.homepage?.languages) {
					await UpdateSponsor(token, _sponsor, workingEvent.homepage.languages);
				}
				dispatch(updateSponsor(_sponsor));

				// update page_module and close if 
				// edit button on displayed sponsor is hit
				if (editDisplayedSponsor) {
					const updatedModules = page_module.modules?.map(s => {
						if (s?.sponsor === _sponsor?.sponsor) {
							return _sponsor;
						}
						return s;
					});
					dispatch(updateHomepageModule({
						...page_module,
						is_edited: true,
						modules: updatedModules
					}));
					setModalOpen(false);
					setEditDisplayedSponsor(false);
					// stop modal from flashing all sponsors while closing
					setTimeout(() => setWorkingSponsor(null), 300);
				} else {
					setWorkingSponsor(null);
				}
			} else {
				const newSponsor = await InsertSponsor(token, _sponsor);
				dispatch(addSponsor(newSponsor));
				setWorkingSponsor(null);
			}
		}
	}

	function setSponsorTitle({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setWorkingSponsor((sponsor: Sponsor | CreateSponsor | null) => {
			if (!sponsor) { return null; }

			return {
				...sponsor,
				name: value,
			};
		});
		updateRequiredField('name', value);
	}

	function setDescription({
		target: { value },
	}: React.ChangeEvent<HTMLTextAreaElement>) {
		setWorkingSponsor((sponsor: Sponsor | CreateSponsor | null) => {
			if (!sponsor) { return null; }

			return {
				...sponsor,
				description: updateTranslateKey({
					translateString: sponsor.description,
					input: value,
					language,
					baseLanguage: "en" as LanguagesAbbr //locking base language as english since this is a global item and can be used across events (and their sessions may have different base languages)
				}),
			};
		});
	}

	const imageName = useMemo(() => {
		if (workingSponsor?.image) {
			const parts = workingSponsor.image.split('/');
			return parts[parts.length - 1];
		} else {
			return '';
		}
	}, [workingSponsor?.image]);

	function removeImage() {
		setWorkingSponsor((sponsor: Sponsor | CreateSponsor | null) => {
			if (!sponsor) { return null; }

			return {
				...sponsor,
				image: '',
			};
		});
	}

	function setEnableCta(value: string, isOn: boolean) {
		setWorkingSponsor((sponsor: Sponsor | CreateSponsor | null) => {
			if (!sponsor) { return null; }

			return {
				...sponsor,
				enable_cta: isOn,
			};
		});
	}

	function handleSearch({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setSearchTerm(value);
	}

	function createNewSponsor() {
		setWorkingSponsor(GetDefaultSponsor());
	}

	function toggleSponsor(sponsor: Sponsor, isOn: boolean) {
		if (!token) return;

		let selectedModules = [...selectedSponsors];
		if (isOn) {
			if (workingEvent?.homepage?.languages) {
				UpdateSponsor(token, sponsor, workingEvent.homepage.languages).then(result => {
					dispatch(updateSponsor(result));
				});
			}

			selectedModules = [...selectedModules, sponsor.sponsor];
		} else {
			selectedModules = selectedModules.filter(
				(module: number) => module !== sponsor.sponsor
			);
		}

		setSelectedSponsors(selectedModules);
	}

	async function handleFile(file: File | FileList) {
		if (!user || !token) return;

		const _file = file as File;
		setUploading(true);

		const url = await UploadFile(user, token, _file);

		setWorkingSponsor((sponsor: Sponsor | CreateSponsor | null) => {
			if (!sponsor) { return null; }

			return {
				...sponsor,
				image: url,
			};
		});
		setUploading(false);
		if (editSponsorImage) closeImageEditor();
	}

	async function handleEditImage(file: File | FileList) {
		setEditSponsorImage(file);
	}

	function removeSponsor() {
		if (sponsorToRemove && page_module.modules) {
			dispatch(
				updateHomepageModule({
					...page_module,
					modules: page_module.modules.filter(
						(module: Sponsor) =>
							module.sponsor !== sponsorToRemove.sponsor
					),
					content_modules:
						page_module.content_modules?.filter(
							(module: number) =>
								module !== sponsorToRemove.sponsor
						) ?? [],
				})
			);
		}

		closeDeleteModal();
	}

	function renderSponsorEditor(_workingSponsor: Sponsor | CreateSponsor) {
		return displayImageEditor ?
			<ImageEditor
				originalImage={_workingSponsor.image}
				onFinished={handleEditImage}
			/>
			:
			<div className="working-sponsor">
				<TextInput
					placeholder={'Enter Sponsor Name'}
					label={'Sponsor Name*'}
					onChange={setSponsorTitle}
					onBlur={() => {
						updateRequiredField('name', _workingSponsor?.name);
					}}
					defaultValue={_workingSponsor.name ?? ''}
					valid={requiredFields.name}
				/>
				<Textarea
					placeholder="Describe your sponsor"
					label="Description"
					defaultValue={
						_workingSponsor.description[language] as string ?? _workingSponsor.description?.base
					}
					onChange={setDescription}
				/>
				<div className="sponsor-image">
					{_workingSponsor.image ? (
						<div className="sponsor-image-data">
							<div className="image-container">
								<div className="left-col">
									<img
										src={_workingSponsor.image}
										alt={_workingSponsor.image}
									/>
									<div className="image-data">
										<span>{imageName}</span>
										{/* designs call for the file size which will go here. Need to figure out how to get that from S3 */}
									</div>
								</div>
								<div className="image-actions">
									<button
										className="no-style"
										onClick={() => setDisplayImageEditor(true)}
									>
										<Icon
											name={ICONS.EDIT}
											size={16}
											color={COLORS.WHITE}
										/>
									</button>
									<button
										className="no-style"
										onClick={removeImage}
									>
										<Icon
											name={ICONS.TRASH_OUTLINE}
											size={16}
											color={COLORS.WHITE}
										/>
									</button>
								</div>
							</div>
						</div>
					) : (
						<>
							<div className={
								classNames("sponsor-image",
									{ error: requiredFields.image === Validation.error })}
							>
								{uploading ? (
									<WaitingIndicator />
								) : (
									<LargeButton
										title="Upload Image*"
										allowedFileTypes={[
											'image/jpeg',
											'image/png',
											'image/gif',
										]}
										subtitle={
											'Allowed filetypes are .jpg, .png, and .gif, and up to 10MB'
										}
										onFile={handleFile}
										style={{ marginBottom: 32 }}
									/>
								)}
							</div>
						</>
					)}
				</div>
				<div className="field-group switch">
					<Switch
						value={'use-cta'}
						on={_workingSponsor.enable_cta}
						onClick={setEnableCta}
					/>
					<label>Enable Button</label>
				</div>
				{_workingSponsor.enable_cta && (
					<CallToActionForm
						onWorkingCTAChange={workingCTA => setWorkingSponsor({ ..._workingSponsor, call_to_action: workingCTA })}
						workingCTA={_workingSponsor.call_to_action}
						language={language}
						baseLanguage={baseLanguage}
					/>
				)}
			</div>;
	}

	useEffect(() => {
		if (token && user) {
			dispatch(loadSponsors(user.active_channel, token));
		}
	}, [dispatch, token, user]);

	useEffect(() => {
		setVisibleSponsors(sponsors);
	}, [sponsors]);

	const { custom_heading, description, title } = getTextValue(content, language);
	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;

	const controlsDisplay = {
		editLayout: true,
		editColors: true,
		addContent: true,
		changeSectionPosition: true,
	};

	return page_module.is_on ? (
		<section
			ref={ref}
			className={classNames(
				'editor-wrapper sponsors',
				`sponsors-${layout_type}`,
				backgroundColor,
				template
			)}
		>
			<Controls
				module={page_module}
				openContentModal={openModal}
				contentButtonText={"Add Sponsors"}
				display={controlsDisplay}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={backgroundImg}>
				<div className="sponsors-text-wrapper">
					<EditableTextSlim
						className="sponsors-custom-heading"
						tagType="p"
						value={t(`${namespace}:sponsors.${page_module.id}.heading`, t(`${namespace}:sponsors.heading`, custom_heading))}
						onChange={handleTextChange('custom_heading')}
						defaultColor={customHeadingDefaultColor}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
						elementName={EContentColorOverrideNames.custom_heading}
						onOverrideChange={handleStyleOverrideChange('custom_heading')}
					/>
					<EditableTextSlim
						className="sponsors-title"
						tagType="h2"
						value={t(`${namespace}:sponsors.${page_module.id}.title`, t(`${namespace}:sponsors.title`, title))}
						onChange={handleTextChange('title')}
						colorOverride={styling_overrides?.content?.title?.color}
						defaultColor={ColorKeys.bodyTextColor}
						elementName={EContentColorOverrideNames.title}
						onOverrideChange={handleStyleOverrideChange('title')}
					/>
					<EditableTextDraftQuill
						className="sponsors-description"
						value={t(`${namespace}:sponsors.${page_module.id}.description`, t(`${namespace}:sponsors.description`, description))}
						onChange={handleTextChange('description')}
						styleOverrides={styling_overrides?.content?.description}
						defaultColor={ColorKeys.bodyTextColor}
						elementName={EContentColorOverrideNames.description}
						onOverrideChange={handleStyleOverrideChange('description')}
						useNew={workingEvent?.settings?.content_editor === ContentEditors.quill}
						defaultTextAlignment={getDefaultModuleAlignment(page_module, template)}
					/>
				</div>
				{page_module.modules && page_module.modules.length > 0 ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToParentElement]}
						autoScroll={false}
					>
						<SortableContext
							items={page_module.modules.map((module: Sponsor) =>
								String(module.sponsor)
							)}
							strategy={rectSortingStrategy}
						>
							<ul className="sponsor-cards-container">
								{page_module.modules?.map(
									(sponsor: Sponsor, index: number) => (
										<SponsorComponent
											sponsor={sponsor}
											key={index}
											id={String(sponsor.sponsor)}
											editSponsor={editSponsor}
											checkDeleteSponsor={
												checkDeleteSponsor
											}
											template={template}
											handleScroll={handleScroll}
											language={language}
										/>
									)
								)}
							</ul>
						</SortableContext>
					</DndContext>
				)
					: <LargeButton
						title="Add Sponsor"
						onClick={openModal}
						style={{ width: "100%", marginTop: '20px' }}
						nonUpload
					/>
				}
			</div>

			<ModalComponent
				closeable={false}
				cancellable={false}
				open={modalOpen}
				onRequestClose={closeModal}
				title={modalTitle}
				footer={
					<>
						<button onClick={cancel} style={{ marginRight: 16 }}>
							Cancel
						</button>
						<button
							onClick={finishedAddingSponsor}
							className="primary"
						>
							{uploading ? <WaitingIndicator /> : 'Done'}
						</button>
					</>
				}
			>
				{workingSponsor ? renderSponsorEditor(workingSponsor) : (
					<div className="select-sponsors">
						<div className="sponsor-search">
							<TextInput
								onChange={handleSearch}
								placeholder={'Search Sponsors...'}
								icon={ICONS.SEARCH}
								size={'small'}
							/>

							<button
								className="small"
								onClick={createNewSponsor}
								disabled={language !== baseLanguage}
							>
								<Icon
									name={ICONS.ADD_PRODUCT}
									size={14}
									color={COLORS.DEFAULT_GRAY}
								/>{' '}
								Add Sponsor
							</button>
							{language !== baseLanguage && (
								<div className="sponsor-tooltip">
									<Tooltip tooltip="Sponsors may only be created in the base language">
										<Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12} />
									</Tooltip>
								</div>
							)}
						</div>

						<div className="sponsors-list">
							{visibleSponsors.map((sponsor: Sponsor) => (
								<AddSponsor
									selected={selectedSponsors}
									onClick={toggleSponsor}
									sponsor={sponsor}
									editSponsor={editSponsor}
									key={sponsor.sponsor}
								/>
							))}
						</div>
					</div>
				)}
			</ModalComponent>
			<ModalComponent
				open={confirmingDelete}
				title={'Remove Sponsor?'}
				onRequestClose={closeDeleteModal}
				cancellable
				closeable={false}
			>
				<>
					<p>
						Are you sure you want to remove{' '}
						<strong>{sponsorToRemove?.name}</strong>?
					</p>

					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginTop: 32,
						}}
					>
						<button onClick={closeDeleteModal} style={{ marginRight: 16 }}>Cancel</button>
						<button className="primary" onClick={removeSponsor}>Remove</button>
					</div>
				</>
			</ModalComponent>
		</section>
	) : null;
});

EditorSponsors.displayName = 'EditorSponsors';

export default EditorSponsors;
